import type { CSSProperties } from 'react';

/**
 * Parent form state updater type. The parent's full state shape varies between callers,
 * but the callback always uses spread ({ ...prev, field: value }) to preserve all fields.
 * TypeScript's SetStateAction is invariant, so the parent type cannot be expressed here
 * without a generic component. Using a minimal callback signature instead.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- parent state type varies; see comment above
type ParentFormUpdater = (fn: (prev: any) => any) => void;

interface CustomerFormBasicDetailsProps {
  formData: {
    customer_type: string;
    company_name: string;
    first_name: string;
    last_name: string;
    customer_name: string;
    email: string;
    phone: string;
    mobile: string;
  };
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  setFormData: ParentFormUpdater;
  setDisplayNameManual: (val: boolean) => void;
  displayNameManual: boolean;
  duplicateCustomer: { id?: string; customer_name?: string; email?: string } | null;
  selectedCustomer: { id?: string } | null;
  checkingDuplicate: boolean;
  styles: Record<string, CSSProperties>;
}

export default function CustomerFormBasicDetails({
  formData,
  handleChange,
  setFormData,
  setDisplayNameManual,
  displayNameManual,
  duplicateCustomer,
  selectedCustomer,
  checkingDuplicate,
  styles,
}: CustomerFormBasicDetailsProps) {
  return (
    <div style={styles.section}>
      <div style={styles.sectionHeader}>
        <span style={styles.sectionTitle}>Basic Details</span>
        <span style={styles.required}>* Required</span>
      </div>

      <div style={styles.sectionContent}>
        {/* Customer Type Toggle */}
        <div style={styles.toggleContainer}>
          <button
            type="button"
            style={{
              ...styles.toggleBtn,
              ...(formData.customer_type === 'individual' ? styles.toggleBtnActive : {}),
            }}
            onClick={() => {
              setFormData(prev => ({ ...prev, customer_type: 'individual' }));
              setDisplayNameManual(false);
            }}
          >
            Consumer
          </button>
          <button
            type="button"
            style={{
              ...styles.toggleBtn,
              ...(formData.customer_type === 'business' ? styles.toggleBtnActive : {}),
            }}
            onClick={() => {
              setFormData(prev => ({ ...prev, customer_type: 'business' }));
              setDisplayNameManual(false);
            }}
          >
            Business
          </button>
        </div>

        {/* Company Name (B2B only) */}
        {formData.customer_type === 'business' && (
          <div style={styles.formGroup}>
            <label style={styles.label}>Company Name *</label>
            <input
              type="text"
              name="company_name"
              style={styles.input}
              value={formData.company_name}
              onChange={handleChange}
              placeholder="Enter company name"
            />
          </div>
        )}

        {/* Customer First Name / Last Name */}
        <div style={styles.row}>
          <div style={styles.formGroup}>
            <label style={styles.label}>
              First Name {formData.customer_type === 'individual' ? '*' : ''}
            </label>
            <input
              type="text"
              name="first_name"
              style={styles.input}
              value={formData.first_name}
              onChange={handleChange}
              placeholder="First name"
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Last Name {formData.customer_type === 'individual' ? '*' : ''}
            </label>
            <input
              type="text"
              name="last_name"
              style={styles.input}
              value={formData.last_name}
              onChange={handleChange}
              placeholder="Last name"
            />
          </div>
        </div>

        {/* Display Name */}
        <div style={styles.formGroup}>
          <label style={styles.label}>
            Display Name *
            <span style={styles.labelHint}> (shown on invoices)</span>
          </label>
          <input
            type="text"
            name="customer_name"
            style={styles.input}
            value={formData.customer_name}
            onChange={handleChange}
            placeholder="Auto-generated from name above"
          />
          {!displayNameManual && (
            <div style={styles.helpText}>Auto-generated. Edit to customize.</div>
          )}
        </div>

        {/* Email */}
        <div style={styles.formGroup}>
          <label style={styles.label}>Email *</label>
          <input
            type="email"
            name="email"
            style={{
              ...styles.input,
              ...(duplicateCustomer && !selectedCustomer ? styles.inputWarning : {}),
            }}
            value={formData.email}
            onChange={handleChange}
            placeholder="email@example.com"
          />
          {checkingDuplicate && <div style={styles.helpText}>Checking...</div>}
        </div>

        {/* Phone & Mobile */}
        <div style={styles.row}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Phone</label>
            <input
              type="tel"
              name="phone"
              style={styles.input}
              value={formData.phone}
              onChange={handleChange}
              placeholder="Phone number"
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Mobile</label>
            <input
              type="tel"
              name="mobile"
              style={styles.input}
              value={formData.mobile}
              onChange={handleChange}
              placeholder="Mobile number"
            />
          </div>
        </div>
      </div>{/* End sectionContent */}
    </div>
  );
}
